import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  MenuItem,
  FormGroup,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Chip,
  Box,
  OutlinedInput,
  InputLabel,
  FormControl,
  Select,
  ListItemText,
} from "@mui/material";
import {
  ButtonWrapper,
  customiseMaterialUITextBoxStyle,
  customStyleButtonSelection,
  formGroupStyle,
  secondaryButtonStyles,
} from "utility/styles";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { userOnboardingValidations } from "utility/form-validations";
import {
  EMAIL_ERROR,
  EMAIL_VALIDATION_FAILED,
  goBackText,
  submitText,
  USER_OFFBOARDING_FLOW,
  USER_ONBOARDING,
  USER_ONBOARDING_FLOW,
} from "utility/constants";
import { FormError, FormSuggestion } from "shared-compoents/form-components";
import {
  buttonSelectionWrapperStyle,
  GridElement,
  GridElementFullWidth,
  OnboardingFormGrid,
  OnboardingFormWrapper,
  ServicesWrapper,
  selectMenuProps,
  StyledMenuItem,
  StyledMenuItemCheckbox,
  ServicesWrapperTitle,
} from "./style";
import {
  callToLoadUserOnboardingFormInitialData,
  callToSubmitUserOnboardForm,
  setIsUserOnboarded,
} from "./ducks/slice";
import {
  getPodList,
  getRoles,
  getIsUserOnboarded,
  getIsSubmitingUserOnboardForm,
} from "./ducks/selectors";
import { RootState } from "api-store/store";
import {
  LoadingBtn,
  MandatoryStarWrapper,
  MidContainer,
  PageDescription,
  PageSubTitle,
  PageTitle,
  PageTopSection,
  PageWrapper,
} from "utility/styled-components";
import SageMakerIcon from "assets/SageMakerIcon";
import DataBricksIcon from "assets/DataBricksIcon";
import { ServicesEnum } from "utility/types";
import { useOnboardingUserServices } from "./ducks/hooks";
import { validateEmailCustomYupValidation } from "pages/mlops/ducks/utils";
import {
  WorkflowItem,
  WorkflowItemConnector,
  WorkflowItemDown,
  WorkflowItemIcon,
  WorkflowItemIconWrapper,
  WorkflowItemTop,
  WorkflowWrapper,
} from "shared-compoents/workflow-items/style";
import { dropdownSelectProps } from "utility/helper";
import { getFilteredRoles } from "utility/helper";

const UserOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [action, setAction] = useState("add");
  const [emailError, setEmailError] = useState("");
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const dispatch = useDispatch();
  const podList = useSelector(getPodList);
  const roles = useSelector(getRoles) || [];
  const isUserOnboarded = useSelector(getIsUserOnboarded);
  const isSubmitingUserOnboardForm = useSelector(getIsSubmitingUserOnboardForm);
  const userData = useSelector((state: RootState) => state.userData);
  const isMounted = useRef(false);
  const email = userData.user_name.replace("tmnatest", "toyota");

  const { register, handleSubmit, setValue, formState, setFocus } = useForm({
    resolver: yupResolver(userOnboardingValidations),
    values: {
      name: userData.name,
      email,
      pod: "",
      role: "",
      comment: "",
      services: [],
      businessJustification: "",
      description: "",
    },
    mode: "onChange",
  });
  const { services } = useOnboardingUserServices({
    role: selectedRoles.join(","),
    roles,
  });
  const { errors, isValidating } = formState;

  const onSubmit = (formData: any) => {
    if (emailError || isValidatingEmail) {
      setFocus("email");
      return;
    }
    const podName = podList.find(
      (podItem) => podItem.sys_id === formData.pod
    )?.u_primary;

    const roleIds: string[] = formData.role.split(",");
    const rolesObject: { [key: string]: string } = {};
    roleIds.forEach((roleId: string) => {
      const roleItem = roles.find((r) => r.sys_id === roleId.trim());
      if (roleItem) { rolesObject[roleItem.u_primary] = roleId.trim(); }
    });

    if (podName && Object.keys(rolesObject).length > 0) {
      dispatch(
        callToSubmitUserOnboardForm({
          v_tdsp_user_would_you_like_to_add_or_remove_a_user: action,
          v_tdsp_point_of_contact: formData.email,
          v_tdsp_description: formData.description,
          v_tdsp_business_justification: formData.businessJustification,
          v_tdsp_pod_needed: { [podName]: formData.pod },
          v_tdsp_user_select_role_s: rolesObject,
          v_opened_by: userData.user_name.replace("tmnatest", "toyota"),
        })
      );
    }
  };

  const handleActionButton = (
    _: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    if (newAlignment !== null) {
      setAction(newAlignment);
    }
    // if (role === "all") {
    //   setValue("role", "", { shouldValidate: true });
    // }
  };

  const handleGoBack = () => {
    navigate("/self-service-portal");
  };

  const handleRoleChange = (event: any) => {
    const value = event.target.value;
    const roleIds = typeof value === "string" ? value.split(",") : value;
    setSelectedRoles(roleIds);
    setValue("role", roleIds.join(","), { shouldValidate: true });
  };

  useEffect(() => {
    if (!isMounted.current) {
      dispatch(callToLoadUserOnboardingFormInitialData());
      isMounted.current = true;
    }
    return () => {
      dispatch(setIsUserOnboarded(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (isUserOnboarded) {
      navigate("/track-requests");
    }
  }, [isUserOnboarded, navigate]);

  const validatePodAmbassadorEmail = async (email: string) => {
    try {
      if (errors && errors?.email && errors?.email?.message) { return; }
      setIsValidatingEmail(true);
      setEmailError("");
      const isValidPod = await validateEmailCustomYupValidation(email);
      if (!isValidPod) { setEmailError(EMAIL_ERROR); }
      else { setEmailError(""); }
    } catch (error) {
      console.log(JSON.stringify(error));
      setEmailError(EMAIL_VALIDATION_FAILED);
    } finally {
      setIsValidatingEmail(false);
    }
  };
  const currentActionText =
    action === "add"
      ? USER_ONBOARDING.userOnboarding
      : USER_ONBOARDING.userOffBoarding;
  const WORKFLOW_FLOW =
    action === "add" ? USER_ONBOARDING_FLOW : USER_OFFBOARDING_FLOW;
  const PAGE_DESCRIPTION =
    action === "add"
      ? USER_ONBOARDING.userOnboardingDesc
      : USER_ONBOARDING.userOffboardingDesc;

  return (
    <PageWrapper>
      <MidContainer>
        <PageTopSection>
          <PageTitle>{currentActionText}</PageTitle>
          <PageDescription>{PAGE_DESCRIPTION}</PageDescription>
        </PageTopSection>
        {/* <FeatureDetails>
          <FeatureDetailsTitle>
            Here is how User Onboarding will look like:
          </FeatureDetailsTitle>
          <FeatureDetailsList>
            <FeatureDetailsListItem>
              Platform Approval: The request is reviewed, and the basic
              requirements are checked and approved.
            </FeatureDetailsListItem>
            <FeatureDetailsListItem>
              Infrastructure & Access Setup: Set up your roles and permissions
              so you can securely access the tools (like Azure) you’ll be using.
            </FeatureDetailsListItem>
            <FeatureDetailsListItem>
              Tool Access: We give you access to Sagemaker and any other tools
              you need to get going.
            </FeatureDetailsListItem>
          </FeatureDetailsList>
        </FeatureDetails> */}
        <WorkflowWrapper>
          {WORKFLOW_FLOW.map((flowItem, index) => (
            <React.Fragment key={index + 1}>
              <WorkflowItem>
                <WorkflowItemTop>
                  {flowItem.SLA ? "SLA: " + flowItem.SLA : ""}
                </WorkflowItemTop>
                <WorkflowItemIconWrapper>
                  <WorkflowItemIcon src={flowItem.image} />
                </WorkflowItemIconWrapper>
                <WorkflowItemDown>{flowItem.stageTitle}</WorkflowItemDown>
              </WorkflowItem>
              {index < WORKFLOW_FLOW.length - 1 && <WorkflowItemConnector />}
            </React.Fragment>
          ))}
        </WorkflowWrapper>
        <form>
          <OnboardingFormWrapper>
            <PageSubTitle>
              {USER_ONBOARDING.formTitle.replace(
                "{USER_ACTION}",
                currentActionText
              )}
            </PageSubTitle>
            <OnboardingFormGrid>
              <GridElementFullWidth>
                <Stack direction="row" spacing={4}>
                  <ToggleButtonGroup
                    value={action}
                    exclusive
                    onChange={handleActionButton}
                    aria-label="text alignment"
                    sx={buttonSelectionWrapperStyle}
                    disabled={isSubmitingUserOnboardForm}
                  >
                    <ToggleButton
                      value="add"
                      aria-label={USER_ONBOARDING.addUser}
                      sx={customStyleButtonSelection}
                    >
                      {USER_ONBOARDING.addUser}
                    </ToggleButton>
                    <ToggleButton
                      value="remove"
                      aria-label={USER_ONBOARDING.removeUser}
                      sx={{ ...customStyleButtonSelection }}
                    >
                      {USER_ONBOARDING.removeUser}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </GridElementFullWidth>
              <GridElementFullWidth>
                <TextField
                  {...register("name")}
                  label={
                    <>
                      {USER_ONBOARDING.name}{" "}
                      <MandatoryStarWrapper>*</MandatoryStarWrapper>
                    </>
                  }
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  sx={customiseMaterialUITextBoxStyle}
                ></TextField>
                {errors && errors?.name ? (
                  <FormError>{errors?.name.message}</FormError>
                ) : (
                  <></>
                )}
              </GridElementFullWidth>
              <GridElementFullWidth>
                <TextField
                  {...register("email", {
                    onChange: (event) => {
                      setTimeout(
                        async () =>
                          await validatePodAmbassadorEmail(event.target.value),
                        0
                      );
                    },
                  })}
                  id="user-email"
                  label={
                    <>
                      {USER_ONBOARDING.email}{" "}
                      <MandatoryStarWrapper>*</MandatoryStarWrapper>
                    </>
                  }
                  fullWidth
                  margin="normal"
                  sx={customiseMaterialUITextBoxStyle}
                ></TextField>
                {isValidatingEmail && (
                  <FormSuggestion>
                    <CircularProgress size="0.7rem" /> Validating Email...
                  </FormSuggestion>
                )}
                {errors && errors?.email ? (
                  <FormError>{errors?.email.message}</FormError>
                ) : (
                  <>{emailError && <FormError>{emailError}</FormError>}</>
                )}
              </GridElementFullWidth>
              <GridElement>
                <TextField
                  {...register("pod")}
                  label={
                    <>
                      {USER_ONBOARDING.pod}{" "}
                      <MandatoryStarWrapper>*</MandatoryStarWrapper>
                    </>
                  }
                  fullWidth
                  margin="normal"
                  select
                  sx={customiseMaterialUITextBoxStyle}
                  defaultValue=""
                  disabled={isSubmitingUserOnboardForm}
                  SelectProps={dropdownSelectProps}
                >
                  {podList.map((pod) => (
                    <MenuItem value={pod.sys_id}>{pod.u_primary}</MenuItem>
                  ))}
                </TextField>
                {errors && errors?.pod ? (
                  <FormError>{errors?.pod.message}</FormError>
                ) : (
                  <></>
                )}
              </GridElement>
              <GridElement>
                <FormControl
                  fullWidth
                  margin="normal"
                  sx={customiseMaterialUITextBoxStyle}
                >
                  <InputLabel id="role-select-label">
                    {USER_ONBOARDING.userRole}
                    <MandatoryStarWrapper>*</MandatoryStarWrapper>
                  </InputLabel>
                  <Select
                    labelId="role-select-label"
                    multiple
                    value={selectedRoles}
                    onChange={handleRoleChange}
                    input={
                      <OutlinedInput label={USER_ONBOARDING.userRole + " *"} />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const role = roles.find((r) => r.sys_id === value);
                          return (
                            <Chip
                              key={value}
                              label={role?.u_primary || value}
                              size="small"
                              onDelete={() => {
                                const newSelectedRoles = selectedRoles.filter(
                                  (id) => id !== value
                                );
                                setSelectedRoles(newSelectedRoles);
                                setValue("role", newSelectedRoles.join(","), {
                                  shouldValidate: true,
                                });
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    disabled={isSubmitingUserOnboardForm}
                    MenuProps={selectMenuProps}
                  >
                    {getFilteredRoles(roles).map((role) => (
                      <StyledMenuItem key={role.sys_id} value={role.sys_id}>
                        <StyledMenuItemCheckbox
                          checked={selectedRoles.indexOf(role.sys_id) > -1}
                        />
                        <ListItemText primary={role.u_primary} />
                      </StyledMenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors && errors?.role ? (
                  <FormError>{errors?.role.message}</FormError>
                ) : (
                  <></>
                )}
              </GridElement>
              {action === "add" && services.length > 0 && (
                <GridElementFullWidth>
                  <ServicesWrapper>
                    <ServicesWrapperTitle>
                      {USER_ONBOARDING.userServicesMessage}
                    </ServicesWrapperTitle>
                    <FormGroup row sx={formGroupStyle}>
                      {services.map((service) => (
                        <>
                          {service === ServicesEnum.SageMaker && (
                            <SageMakerIcon width="69.96px" height="22px" />
                          )}
                          {service === ServicesEnum.DataBricks && (
                            <DataBricksIcon width="77.96px" height="30px" />
                          )}
                        </>
                      ))}
                    </FormGroup>
                  </ServicesWrapper>
                </GridElementFullWidth>
              )}
              <GridElementFullWidth>
                <TextField
                  {...register("description")}
                  label={
                    <>
                      {USER_ONBOARDING.descriptionLabel}{" "}
                      <MandatoryStarWrapper>*</MandatoryStarWrapper>
                    </>
                  }
                  fullWidth
                  multiline
                  rows={3}
                  sx={customiseMaterialUITextBoxStyle}
                  disabled={isSubmitingUserOnboardForm}
                />
                {errors && errors?.description ? (
                  <FormError>{errors?.description.message}</FormError>
                ) : (
                  <></>
                )}
              </GridElementFullWidth>
              <GridElementFullWidth>
                <TextField
                  {...register("businessJustification")}
                  label={
                    <>
                      {USER_ONBOARDING.businessJustification}{" "}
                      <MandatoryStarWrapper>*</MandatoryStarWrapper>
                    </>
                  }
                  fullWidth
                  multiline
                  rows={3}
                  sx={customiseMaterialUITextBoxStyle}
                  disabled={isSubmitingUserOnboardForm}
                />
                {errors && errors?.businessJustification ? (
                  <FormError>{errors?.businessJustification.message}</FormError>
                ) : (
                  <></>
                )}
              </GridElementFullWidth>
              {/* <GridElementFullWidth>
                 <TextField
                   {...register("comment")}
                   label="Additional Comments"
                   fullWidth
                   multiline
                   rows={2}
                   sx={customiseMaterialUITextBoxStyle}
                 />
                 {errors && errors?.comment ? (
                   <FormError>{errors?.comment.message}</FormError>
                 ) : (
                   <></>
                 )}
               </GridElementFullWidth> */}
            </OnboardingFormGrid>
            <ButtonWrapper>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoBack}
                sx={secondaryButtonStyles}
                disabled={isSubmitingUserOnboardForm}
              >
                {goBackText}
              </Button>
              <LoadingBtn
                onClick={handleSubmit(onSubmit)}
                loading={isSubmitingUserOnboardForm || isValidating}
                loadingIndicator={isValidating ? "Validating" : "Submitting"}
              >
                {isSubmitingUserOnboardForm || isValidating ? "" : submitText}
              </LoadingBtn>
            </ButtonWrapper>
          </OnboardingFormWrapper>
        </form>
      </MidContainer>
    </PageWrapper>
  );
};

export default UserOnboarding;
